import React, { useCallback, useEffect, useState } from "react";
import { Table, message, Tag, Button, Input } from "antd";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

function BalanceVipOrderList({ user }) {
  const [list, setList] = useState({ data: [], current_page: 1 });
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);

  const fetchList = useCallback(
    async (page = 1) => {
      const { token } = user;
      if (!token) {
        return;
      }
      setLoading(true);
      try {
        const res = await api.balanceVip.list.run({
          token,
          query: {
            page,
            count: 10,
            keyword,
          },
        });
        if (res.result) {
          setList(res.data);
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [user, keyword]
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const acceptOrder = async (order) => {
    if (!order?.can_accept) {
      return;
    }
    setAcceptingId(order.id);
    try {
      const res = await api.balanceVip.accept.run({
        token: user.token,
        path: { id: order.id },
      });
      if (res.result) {
        message.success(res.message || i18n.t("order.accept"));
        fetchList(list.current_page || 1);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setAcceptingId(null);
    }
  };

  const columns = [
    {
      title: i18n.t("balanceVip.orderNo"),
      dataIndex: "order_no",
      key: "order_no",
    },
    {
      title: i18n.t("balanceVip.user"),
      key: "user",
      render: (_, row) => (
        <div>
          <div>{row.user?.nickname || "—"}</div>
          <div>{row.user?.mobile || ""}</div>
        </div>
      ),
    },
    {
      title: i18n.t("balanceVip.amount"),
      dataIndex: "price",
      key: "price",
      render: (price) => `¥${(Number(price || 0) / 100).toFixed(2)}`,
    },
    {
      title: i18n.t("balanceVip.discount"),
      dataIndex: "self_discount_percent",
      key: "self_discount_percent",
      render: (value) => (value != null ? `${value}%` : "—"),
    },
    {
      title: i18n.t("balanceVip.paidAt"),
      dataIndex: "paid_at",
      key: "paid_at",
    },
    {
      title: i18n.t("balanceVip.status"),
      key: "status",
      render: (_, row) => {
        if (row.can_accept) {
          return <Tag color="orange">{i18n.t("balanceVip.pendingAccept")}</Tag>;
        }
        return <Tag color="green">{i18n.t("balanceVip.accepted")}</Tag>;
      },
    },
    {
      title: i18n.t("balanceVip.action"),
      key: "action",
      render: (_, row) =>
        row.can_accept ? (
          <Button
            type="primary"
            size="small"
            loading={acceptingId === row.id}
            onClick={() => acceptOrder(row)}
          >
            {i18n.t("order.accept")}
          </Button>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="balance-vip-order-list">
      <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
        <Input
          placeholder={i18n.t("balanceVip.searchPlaceholder")}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onPressEnter={() => fetchList(1)}
          style={{ maxWidth: 280 }}
        />
        <Button onClick={() => fetchList(1)}>{i18n.t("balanceVip.refresh")}</Button>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list.data || []}
        pagination={{
          current: list.current_page || 1,
          total: list.total || 0,
          pageSize: 10,
          onChange: (page) => fetchList(page),
        }}
      />
    </div>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(BalanceVipOrderList);
